import React, { useState, useEffect } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { dashboardAPI } from '../services/api';
import NotificationForm from '../components/NotificationForm';
import PageHeader from '../components/PageHeader';
import DataTable, { Column } from '../components/DataTable';
import SidePanel from '../components/SidePanel';
import StatusBadge from '../components/StatusBadge';
import '../styles/global.css';

const AdminNotifications: React.FC = () => {
  const { user: currentUser } = useAuth();
  const [notifications, setNotifications] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [editingNotification, setEditingNotification] = useState<any>(null);
  const [editNotifIndex, setEditNotifIndex] = useState(-1);
  const [editFormData, setEditFormData] = useState({ title: '', message: '', notification_type: 'info' });

  useEffect(() => { loadNotifications(); }, []);

  const loadNotifications = async () => {
    try {
      setLoading(true);
      setNotifications(await dashboardAPI.getNotifications());
    } catch (err: any) { setError(err.message || 'Ошибка загрузки'); }
    finally { setLoading(false); }
  };

  const handleDelete = async (id: number) => {
    if (!window.confirm('Удалить уведомление?')) return;
    try { await dashboardAPI.deleteNotification(id); loadNotifications(); }
    catch (err: any) { setError(err.message || 'Ошибка удаления'); }
  };

  const openEdit = (n: any) => {
    const idx = notifications.findIndex(x => x.id === n.id);
    setEditingNotification(n);
    setEditNotifIndex(idx);
    setEditFormData({ title: n.title, message: n.message, notification_type: n.notification_type });
  };

  const closePanel = () => {
    setShowCreateForm(false);
    setEditingNotification(null);
    setEditNotifIndex(-1);
  };

  const handleUpdate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await dashboardAPI.updateNotification(editingNotification.id, editFormData as any);
      closePanel();
      loadNotifications();
    } catch (err: any) { setError(err.message || 'Ошибка обновления'); }
  };

  const columns: Column<any>[] = [
    { key: 'id', header: 'ID', width: '55px' },
    {
      key: 'title', header: 'Заголовок',
      render: (n) => <strong style={{ color:'var(--primary-orange)' }}>{n.title}</strong>,
    },
    {
      key: 'message', header: 'Сообщение',
      render: (n) => (
        <span className="cell-truncate" title={n.message} style={{ display:'block', maxWidth:340 }}>
          {n.message}
        </span>
      ),
    },
    {
      key: 'notification_type', header: 'Тип',
      render: (n) => (
        <StatusBadge
          status={n.notification_type === 'profile' ? 'submitted' : 'pending'}
          label={n.notification_type === 'profile' ? 'Профиль' : 'Инфо'}
        />
      ),
    },
    {
      key: 'created_at', header: 'Дата',
      render: (n) => new Date(n.created_at).toLocaleDateString('ru-RU'),
    },
  ];

  const renderExpandedNotif = (n: any) => (
    <div style={{ display: 'flex', gap: '2rem', alignItems: 'flex-start', flexWrap: 'wrap' }}>
      <div style={{ flex: 1 }}>
        <div className="exp-label">Сообщение</div>
        <div className="exp-value" style={{ whiteSpace: 'pre-wrap' }}>{n.message}</div>
      </div>
      <div className="exp-actions" onClick={(e) => e.stopPropagation()}>
        <button className="btn btn-primary btn-sm" onClick={() => openEdit(n)}>Изменить</button>
        <button className="btn btn-danger btn-sm" onClick={() => handleDelete(n.id)}>Удалить</button>
      </div>
    </div>
  );

  if (currentUser?.role !== 'admin')
    return <div className="container"><div className="error">Нет прав доступа</div></div>;

  if (loading) return <div className="loading">Загрузка...</div>;

  return (
    <div className="container" style={{ paddingTop:'1.5rem', paddingBottom:'2rem' }}>
      <PageHeader
        title="Управление уведомлениями"
        actions={
          <button className="btn btn-primary" onClick={() => setShowCreateForm(true)}>
            + Создать уведомление
          </button>
        }
      />
      {error && <div className="error">{error}</div>}

      <DataTable
        columns={columns}
        data={notifications}
        rowKey={(n) => n.id}
        emptyText="Нет уведомлений"
        renderExpanded={renderExpandedNotif}
      />

      {/* Панель: создать уведомление */}
      <SidePanel
        isOpen={showCreateForm}
        onClose={closePanel}
        title="Новое уведомление"
      >
        <NotificationForm onNotificationCreated={() => { closePanel(); loadNotifications(); }} />
      </SidePanel>

      {/* Панель: редактировать уведомление */}
      <SidePanel
        isOpen={!!editingNotification}
        onClose={closePanel}
        title={`Уведомление #${editingNotification?.id}`}
        onPrev={() => openEdit(notifications[editNotifIndex - 1])}
        onNext={() => openEdit(notifications[editNotifIndex + 1])}
        hasPrev={editNotifIndex > 0}
        hasNext={editNotifIndex < notifications.length - 1}
        navLabel={editingNotification ? `${editNotifIndex + 1} из ${notifications.length}` : undefined}
      >
        <form onSubmit={handleUpdate}>
          <div className="form-group">
            <label className="form-label">Заголовок</label>
            <input type="text" className="form-control" value={editFormData.title} onChange={(e) => setEditFormData({...editFormData, title:e.target.value})} required />
          </div>
          <div className="form-group">
            <label className="form-label">Сообщение</label>
            <textarea className="form-control" rows={6} value={editFormData.message} onChange={(e) => setEditFormData({...editFormData, message:e.target.value})} required />
          </div>
          <div className="form-group">
            <label className="form-label">Тип</label>
            <select className="form-control" value={editFormData.notification_type} onChange={(e) => setEditFormData({...editFormData, notification_type:e.target.value})}>
              <option value="info">Инфо</option>
              <option value="profile">Профиль</option>
            </select>
          </div>
          <div className="side-panel__footer">
            <button type="button" className="btn btn-secondary" onClick={closePanel}>Отмена</button>
            <button type="submit" className="btn btn-primary">Сохранить</button>
          </div>
        </form>
      </SidePanel>
    </div>
  );
};

export default AdminNotifications;
